import { motion } from "framer-motion";
import { Sunrise, Sunset, BookOpen, UtensilsCrossed } from "lucide-react";

const SCHEDULE = [
  { icon: Sunrise, title: "Morning Darshan", time: "Saturdays 8:30 – 11:30 AM", note: "Quiet darshan of Baba and Hanuman-ji to begin the weekend" },
  { icon: Sunset, title: "Evening Aarti", time: "Saturdays 5:00 – 8:30 PM", note: "Diya aarti, kirtan and prasad distribution" },
  { icon: BookOpen, title: "Hanuman Chalisa", time: "Every Saturday", note: "Group recitation of the forty verses to Hanuman-ji" },
  { icon: UtensilsCrossed, title: "Bhandara Prasad", time: "Every Saturday", note: "A free sanctified meal shared with all who come — everyone is fed" },
];

export default function AboutTemple() {
  return (
    <section id="about" data-testid="about-section" className="py-20 sm:py-28 bg-[#FDFBF7]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.8 }}
          >
            <p className="eyebrow mb-4">01 · A Sanctuary in Texas</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-amber-950" data-testid="about-heading">
              From the Kumaon Hills to the Heart of Texoma
            </h2>
            <div className="jali-divider my-6 w-48" />
            <p className="text-base leading-relaxed text-stone-700 mb-5">
              Nestled in Denison, Texas — near the shores of Lake Texoma — our mandir at Osho Kaivalya
              Dham is a humble home for Neem Karoli Baba (Maharaj-ji) and Lord Hanuman. What began as the heartfelt
              devotion of a few families has grown into a living sanctuary where seekers of every faith and
              background are welcomed as family.
            </p>
            <p className="text-base leading-relaxed text-stone-700 mb-5">
              At the center of the grounds stands a majestic black granite murti of Lord Hanuman, blessing
              all who arrive. Inside, devotees gather around Baba's garlanded portrait for aarti, kirtan,
              Hanuman Chalisa, and the bhandara — the free community meal that was so dear to Maharaj-ji,
              who said <span className="font-quote italic text-amber-900">"feed everyone, for to feed people is to serve God."</span>
            </p>
            <p className="text-base leading-relaxed text-stone-700">
              Whether you come for five minutes of silence or a full evening of satsang, you are welcome here.
              Come as you are.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="diya-glow rounded-2xl overflow-hidden border border-amber-900/15 shadow-2xl shadow-amber-900/10">
              <img src="/images/hanuman-sunset-2.webp" alt="Lord Hanuman murti on the temple grounds at dusk — shown in full"
                data-testid="about-hanuman-image" className="relative z-10 w-full aspect-[3/4] object-cover" />
            </div>
            <div className="mt-5 bg-white rounded-xl border border-amber-900/15 shadow-lg shadow-amber-900/10 px-5 py-4 max-w-xs" data-testid="sub-ek-card">
              <p className="font-quote italic text-lg text-amber-900 leading-snug">"Sub Ek"</p>
              <p className="text-xs text-stone-500 mt-1 tracking-wide">All is One — Baba's simplest teaching, lived here daily.</p>
            </div>
          </motion.div>
        </div>

        <div className="mt-24 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {SCHEDULE.map((s, i) => (
            <motion.div key={s.title}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.1 }}
              data-testid={`schedule-card-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
              className="group bg-white rounded-2xl border border-amber-900/10 p-6 hover:border-amber-600/40 hover:shadow-lg hover:shadow-amber-900/5 hover:-translate-y-1 transition-all duration-300"
            >
              <s.icon className="h-7 w-7 text-amber-700 mb-4 group-hover:scale-110 transition-transform duration-300" />
              <h3 className="font-semibold text-stone-900 text-base">{s.title}</h3>
              <p className="text-amber-800 text-sm font-medium mt-1">{s.time}</p>
              <p className="text-stone-500 text-sm mt-2 leading-relaxed">{s.note}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
